// Automatic Google Analytics (gtag) impression tracking for buttons.
// tracks when a button is viewed in the screen, but won't track
// if it doesn't show on the screen

const EVENT_NAME = 'button_impression';
const MAX_NAME_LENGTH = 40;
const FALLBACK_NAME = 'unnamed_button';

// broad catch all for buttons
const BUTTON_SELECTOR = 'button, input[type="button"], input[type="submit"], input[type="reset"], [role="button"]';

// id for the button just in case we want this
const GENERATED_ID = /^button\d+$/i;

// don't count if observed, this sets it
const OBSERVED_ATTR = 'data-button-impression-observed';

// user type
function currentUserType() {
  const meta = document.querySelector('meta[name="analytics-user-type"]');
  return (meta && meta.getAttribute('content')) || undefined;
}

function normalizeName(raw) {
  const cleaned = (raw || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_') // non-alphanumerics -> underscore
    .replace(/^_+|_+$/g, ''); // trim leading/trailing underscores

  if (!cleaned) return '';

  return cleaned.slice(0, MAX_NAME_LENGTH).replace(/_+$/g, '');
}

function buttonText(button) {
  if (button.tagName === 'INPUT') {
    return button.value || button.getAttribute('aria-label') || '';
  }
  return button.textContent || button.getAttribute('aria-label') || '';
}

// Returns the first non-empty normalized candidate, in priority order.
function resolveName(button) {
  const candidates = [
    button.getAttribute('data-analytics-name'),
    button.getAttribute('value'),
    buttonText(button),
    button.getAttribute('name'),
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const normalized = normalizeName(candidates[i]);
    if (normalized) return normalized;
  }

  // Fall back to the id only when it is not an auto-generated one.
  if (button.id && !GENERATED_ID.test(button.id)) {
    const normalizedId = normalizeName(button.id);
    if (normalizedId) return normalizedId;
  }

  return FALLBACK_NAME;
}

function recordImpression(button) {
  const name = resolveName(button);
  const payload = {
    button_name: name,
    button_text: buttonText(button).trim().slice(0, 100),
    button_id: button.id || undefined,
    page_path: window.location.pathname,
    user_type: currentUserType(),
  };

  if (typeof window.gtag !== 'function') return;

  window.gtag('event', EVENT_NAME, payload);
}

// Shared observer: fire once per button so we don't count it again
const impressionObserver = new IntersectionObserver((entries, observer) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;

    observer.unobserve(entry.target);
    recordImpression(entry.target);
  });
}, { threshold: 0 });

function observeButton(button) {
  if (button.hasAttribute(OBSERVED_ATTR)) return;

  button.setAttribute(OBSERVED_ATTR, 'true');
  impressionObserver.observe(button);
}

function observeAllButtonsIn(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  // Observe the root itself if it is a button, plus any descendant buttons.
  if (typeof root.matches === 'function' && root.matches(BUTTON_SELECTOR)) {
    observeButton(root);
  }

  root.querySelectorAll(BUTTON_SELECTOR).forEach(observeButton);
}

// Watch for buttons that show after initial load
const mutationObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        observeAllButtonsIn(node);
      }
    });
  });
});

function start() {
  observeAllButtonsIn(document);
  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
